import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBullhorn,
  FaEye,
  FaLayerGroup,
  FaPlus,
  FaSearch,
  FaShieldAlt,
  FaSync,
  FaTrash,
  FaWrench,
} from "react-icons/fa";
import { apihubDelete, apihubGet } from "../../api/apihub";
import { businessPath } from "../../config/businessMenu";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import { hasPermission } from "../../utils/auth";
import StatusCapsule from "../StatusCapsule";

const PAGE_SIZES = [10, 20, 50];
const STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "APPROVED", label: "Approved" },
  { id: "PENDING", label: "Pending" },
  { id: "REJECTED", label: "Rejected" },
];

const getTemplateType = (tpl) => {
  const header = tpl?.components?.find((c) => c.type === "HEADER");
  if (header?.format) return header.format;
  return "TEXT";
};

const formatCreatedAt = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const emptyStats = () => ({
  total: 0,
  marketing: 0,
  auth: 0,
  utility: 0,
  approved: 0,
  pending: 0,
  rejected: 0,
});

const normalizeToken = (value) => String(value || "").trim().toUpperCase();

const countFromList = (list = []) =>
  list.reduce(
    (acc, tpl) => {
      const category = normalizeToken(tpl.category);
      if (category === "MARKETING") acc.marketing += 1;
      else if (category === "AUTHENTICATION") acc.auth += 1;
      else if (category === "UTILITY") acc.utility += 1;

      const status = normalizeToken(tpl.template_status);
      if (status === "APPROVED") acc.approved += 1;
      else if (status === "PENDING") acc.pending += 1;
      else if (status === "REJECTED") acc.rejected += 1;
      return acc;
    },
    { ...emptyStats(), total: list.length },
  );

const statsFromResponse = (res, list) => {
  const p = res?.pagination || {};
  const s = res?.stats || {};
  const fromList = countFromList(list);
  return {
    total: p.totalRecords ?? s.total ?? fromList.total,
    marketing: p.marketingtemplates ?? s.marketing ?? fromList.marketing,
    auth: p.authenticationTemplates ?? s.authentication ?? s.auth ?? fromList.auth,
    utility: p.utilityTemplates ?? s.utility ?? fromList.utility,
    approved: p.approvedtemplates ?? s.approved ?? fromList.approved,
    pending: p.pendingtemplates ?? s.pending ?? fromList.pending,
    rejected: p.rejectedtemplates ?? s.rejected ?? fromList.rejected,
  };
};

// Prevent duplicate "initial load" requests in React 18 dev (StrictMode mounts twice).
// Cache only non-append calls, so load-more behavior can't double-add results.
const templatesFetchCache = new Map();

const FetchAllTemplates = () => {
  const { phoneNoId, wabaId, projectId } = useProjectPhone();
  const canCreate = hasPermission("whatsAppTemplate_Create");
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(emptyStats);
  const [search, setSearch] = useState("");
  const [statusTab, setStatusTab] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState("");
  const [preview, setPreview] = useState(null);

  const fetchTemplates = useCallback(
    async (pageNum, searchVal, limitVal, append = false, isSync = false) => {
      if (!phoneNoId) return;
      if (isSync) setSyncing(true);
      else setLoading(true);
      setError("");
      try {
        const qs = new URLSearchParams({
          page: String(pageNum),
          limit: String(limitVal),
          sortBy: "createdAt",
          sortOrder: "desc",
          ...(searchVal ? { search: searchVal } : {}),
        });

        const cacheKey =
          !append && !isSync
            ? `waTemplates|${phoneNoId}|p:${pageNum}|l:${limitVal}|q:${searchVal || ""}`
            : null;

        if (cacheKey && templatesFetchCache.has(cacheKey)) {
          const cached = await templatesFetchCache.get(cacheKey);
          const list = cached?.data || [];
          const p = cached?.pagination || {};
          setTemplates(list);
          setHasMore(Boolean(p.hasMore));
          setStats(statsFromResponse(cached, list));
          return;
        }

        const fetchPromise = apihubGet(
          `/getInteractedCustomerDetail/gettotaltemplatesbyphonenumberid/${phoneNoId}?${qs}&projectId=${encodeURIComponent(
            projectId || "",
          )}`,
        );

        if (cacheKey) templatesFetchCache.set(cacheKey, fetchPromise);

        const res = await fetchPromise;
        const list = res?.data || [];
        setTemplates((prev) => {
          const next = append ? [...prev, ...list] : list;
          setHasMore(Boolean(res?.pagination?.hasMore));
          setStats(statsFromResponse(res, next));
          return next;
        });
      } catch (err) {
        if (!append) {
          setTemplates([]);
          setStats(emptyStats());
        }
        setError(err.message || "Failed to load templates");
      } finally {
        setLoading(false);
        setSyncing(false);
      }
    },
    [phoneNoId, projectId],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchTemplates(1, search.trim(), limit, false);
    }, 400);
    return () => clearTimeout(timer);
  }, [search, limit, fetchTemplates]);

  const filteredTemplates = useMemo(() => {
    if (statusTab === "all") return templates;
    return templates.filter(
      (t) => normalizeToken(t.template_status) === statusTab,
    );
  }, [templates, statusTab]);

  const statusCounts = useMemo(
    () => ({
      all: stats.total,
      APPROVED: stats.approved,
      PENDING: stats.pending,
      REJECTED: stats.rejected,
    }),
    [stats],
  );

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchTemplates(next, search.trim(), limit, true);
  };

  const onSync = () => {
    setPage(1);
    fetchTemplates(1, search.trim(), limit, false, true);
  };

  const onDelete = async (tpl) => {
    if (!tpl?.name) return;
    if (!phoneNoId && !wabaId) {
      setError("WhatsApp is not connected for this project.");
      return;
    }
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    setDeleting(tpl.name);
    setError("");
    try {
      await apihubDelete("/getInteractedCustomerDetail/deletetemplate", {
        name: tpl.name,
        phoneNumberId: phoneNoId,
        wabaId,
        projectId,
      });
      for (const key of [...templatesFetchCache.keys()]) {
        if (key.startsWith(`waTemplates|${phoneNoId}|`)) {
          templatesFetchCache.delete(key);
        }
      }
      setTemplates((prev) => prev.filter((t) => t.name !== tpl.name));
      setStats((prev) => {
        const status = normalizeToken(tpl.template_status);
        const category = normalizeToken(tpl.category);
        return {
          ...prev,
          total: Math.max(0, prev.total - 1),
          marketing:
            category === "MARKETING" ? Math.max(0, prev.marketing - 1) : prev.marketing,
          auth:
            category === "AUTHENTICATION" ? Math.max(0, prev.auth - 1) : prev.auth,
          utility:
            category === "UTILITY" ? Math.max(0, prev.utility - 1) : prev.utility,
          approved:
            status === "APPROVED" ? Math.max(0, prev.approved - 1) : prev.approved,
          pending:
            status === "PENDING" ? Math.max(0, prev.pending - 1) : prev.pending,
          rejected:
            status === "REJECTED" ? Math.max(0, prev.rejected - 1) : prev.rejected,
        };
      });
    } catch (err) {
      setError(err.message || "Delete failed");
    } finally {
      setDeleting("");
    }
  };

  const previewBody =
    preview?.components?.find((c) => c.type === "BODY")?.text || "";

  return (
    <div className="templates-page">
      <header className="templates-header">
        <div className="templates-header-text">
          <h1>
            <FaLayerGroup aria-hidden />
            Templates
          </h1>
          <p>
            Meta Templates are pre-approved WhatsApp Message formats/Content that
            you want to send to your customers about Offers, Updates, Notifications
            etc. Whether Manually or Automated.
          </p>
        </div>
        <div className="templates-header-actions">
          <button
            type="button"
            className="templates-sync-btn"
            onClick={onSync}
            disabled={syncing || loading}
          >
            <FaSync className={syncing ? "templates-spin" : ""} />
            {syncing ? "Syncing…" : "Sync"}
          </button>
          <Link
            to={businessPath(projectId, "templatelib")}
            className="templates-add-btn templates-library-btn"
          >
            <FaPlus />
            Template Library
          </Link>
          <Link
            to={businessPath(projectId, "createtemplate")}
            className={`templates-add-btn${canCreate ? "" : " is-disabled"}`}
            aria-disabled={!canCreate}
            onClick={(e) => {
              if (!canCreate) e.preventDefault();
            }}
            title={
              canCreate
                ? "Create a new WhatsApp template"
                : "whatsAppTemplate_Create permission required"
            }
          >
            <FaPlus />
            Create Template
          </Link>
        </div>
      </header>

      <section className="templates-stats">
        <article className="templates-stat-card is-all">
          <div className="templates-stat-icon">
            <FaLayerGroup />
          </div>
          <div className="templates-stat-copy">
            <span>All Templates</span>
            <strong>{stats.total}</strong>
          </div>
        </article>
        <article className="templates-stat-card is-marketing">
          <div className="templates-stat-icon">
            <FaBullhorn />
          </div>
          <div className="templates-stat-copy">
            <span>Marketing Templates</span>
            <strong>{stats.marketing}</strong>
          </div>
        </article>
        <article className="templates-stat-card is-auth">
          <div className="templates-stat-icon">
            <FaShieldAlt />
          </div>
          <div className="templates-stat-copy">
            <span>Authentication Templates</span>
            <strong>{stats.auth}</strong>
          </div>
        </article>
        <article className="templates-stat-card is-utility">
          <div className="templates-stat-icon">
            <FaWrench />
          </div>
          <div className="templates-stat-copy">
            <span>Utility Templates</span>
            <strong>{stats.utility}</strong>
          </div>
        </article>
      </section>

      <section className="templates-toolbar">
        <div className="templates-filters" role="tablist" aria-label="Template status">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={statusTab === tab.id}
              className={`templates-filter-btn ${
                statusTab === tab.id ? "active" : ""
              }`}
              onClick={() => setStatusTab(tab.id)}
            >
              {tab.label}
              <span className="templates-filter-count">
                {statusCounts[tab.id] ?? 0}
              </span>
            </button>
          ))}
        </div>
        <div className="templates-search">
          <FaSearch className="templates-search-icon" aria-hidden />
          <input
            type="search"
            placeholder="Search template..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search templates"
          />
        </div>
      </section>

      {error && <p className="templates-error">{error}</p>}

      <section className="templates-table-section">
        <div className="templates-table-wrap">
          <table className="templates-table">
            <thead>
              <tr>
                <th>Template Name</th>
                <th>Template ID</th>
                <th>Category</th>
                <th>Type</th>
                <th>Created At</th>
                <th>Status</th>
                <th>Quality</th>
                <th className="biz-col-actions">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && filteredTemplates.length === 0 ? (
                <tr>
                  <td colSpan={8} className="templates-table-empty">
                    Loading templates…
                  </td>
                </tr>
              ) : filteredTemplates.length === 0 ? (
                <tr>
                  <td colSpan={8} className="templates-table-empty">
                    No templates found.
                  </td>
                </tr>
              ) : (
                filteredTemplates.map((tpl) => (
                  <tr key={tpl._id || tpl.template_id || tpl.name}>
                    <td className="templates-name">{tpl.name || "—"}</td>
                    <td className="templates-id">
                      {tpl.template_id || tpl.id || tpl._id || "—"}
                    </td>
                    <td>{(tpl.category || "—").toUpperCase()}</td>
                    <td>{getTemplateType(tpl)}</td>
                    <td>{formatCreatedAt(tpl.createdAt)}</td>
                    <td>
                      <StatusCapsule
                        value={(tpl.template_status || "—").toUpperCase()}
                      />
                    </td>
                    <td>{(tpl.quality || "UNKNOWN").toUpperCase()}</td>
                    <td className="biz-col-actions">
                      <div className="biz-table-actions templates-row-actions">
                        <button
                          type="button"
                          className="templates-view-btn"
                          aria-label={`View ${tpl.name}`}
                          onClick={() => setPreview(tpl)}
                        >
                          <FaEye />
                        </button>
                        <button
                          type="button"
                          className="templates-delete-btn"
                          aria-label={`Delete ${tpl.name}`}
                          disabled={deleting === tpl.name}
                          onClick={() => onDelete(tpl)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="templates-footer">
          <label className="templates-per-page">
            Per page
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          {hasMore && (
            <button
              type="button"
              className="templates-load-more"
              onClick={loadMore}
              disabled={loading}
            >
              {loading ? "Loading…" : "Load more"}
            </button>
          )}
        </div>
      </section>

      {preview && (
        <div
          className="templates-modal-backdrop"
          role="presentation"
          onClick={() => setPreview(null)}
        >
          <div
            className="templates-modal"
            role="dialog"
            aria-labelledby="templates-preview-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="templates-modal-header">
              <h2 id="templates-preview-title">{preview.name}</h2>
              <button
                type="button"
                className="templates-modal-close"
                onClick={() => setPreview(null)}
              >
                ×
              </button>
            </header>
            <div className="templates-modal-meta">
              <span>{(preview.category || "—").toUpperCase()}</span>
              <span>{getTemplateType(preview)}</span>
              <span>{(preview.template_status || "—").toUpperCase()}</span>
              <span>{preview.language || "—"}</span>
            </div>
            <div className="templates-modal-chat">
              <div className="templates-modal-bubble">
                <pre className="templates-modal-body">
                  {previewBody || "No body content."}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FetchAllTemplates;
